import React, { useCallback, useMemo } from 'react';
import { useChatContext } from '../context/ChatContext';
import { useAnnouncer } from '../context/AccessibilityContext';
import { useChatActions } from '../hooks/useChatActions';
import { Reference, Message, TypingData } from '../types';
import MessageList from './MessageList';
import VirtualizedMessageList from './VirtualizedMessageList';

import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';

import './ChatContainer.css';

interface ChatContainerProps {
  className?: string;
  onRetryConnection?: () => void;
  onSendMessage?: (content: string) => Promise<any>;
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  onVoiceData?: (audioBlob: Blob) => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ 
  className = '', 
  onRetryConnection,
  onSendMessage,
  onVoiceStart,
  onVoiceStop,
  onVoiceData,
}) => {
  const { state } = useChatContext();
  const announcer = useAnnouncer();
  const { addMessage, setTyping } = useChatActions();



  const handleReferenceClick = (ref: Reference) => {
    // TODO: Implement reference click handling
    console.log('Reference clicked:', ref);
    announcer.announce(`Opening reference: ${ref.title}`, 'polite');
  };

  const handleSendMessage = useCallback(async (content: string) => {
    try {
      if (onSendMessage) {
        // Use the provided send message function (SSE-based)
        await onSendMessage(content);
        console.log('Text message sent via SSE');
      } else {
        // Fallback to direct API call
        const response = await fetch('http://localhost:3001/api/chat/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: content,
            sender: state.currentUser
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Message sent successfully:', result);
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Fallback: add message locally if server is unavailable
      const newMessage: Message = {
        id: `msg_${Date.now()}`,
        content,
        sender: state.currentUser,
        timestamp: new Date(),
      };
      addMessage(newMessage);
      
      // Announce error to user
      announcer.announce('Failed to send message. Added to local chat only.', 'assertive');
    }
  }, [onSendMessage, addMessage, state.currentUser, announcer]);

  const handleVoiceData = useCallback((audioBlob: Blob) => {
    console.log('Voice data received:', audioBlob);
    
    if (onVoiceData) {
      // Use the provided voice data handler (WebSocket-based)
      onVoiceData(audioBlob);
      console.log('Voice data sent via WebSocket');
    } else {
      // Fallback: create a placeholder message
      const newMessage: Message = {
        id: `msg_${Date.now()}`,
        content: '[Voice message]',
        sender: state.currentUser,
        timestamp: new Date(),
      };
      addMessage(newMessage);
    }
  }, [onVoiceData, addMessage, state.currentUser]);

  const handleTypingChange = useCallback((isTyping: boolean) => {
    const typingData: TypingData = {
      userId: state.currentUser,
      isTyping,
    };
    setTyping(typingData);
  }, [setTyping, state.currentUser]);

  // Memoize reference click handler to prevent unnecessary re-renders
  const memoizedHandleReferenceClick = useCallback((ref: Reference) => {
    console.log('Reference clicked:', ref);
    announcer.announce(`Opening reference: ${ref.title}`, 'polite');
  }, [announcer]);

  // Determine whether to use virtualization based on message count
  const shouldUseVirtualization = useMemo(() => {
    return state.messages.length > 100; // Use virtualization for large message lists
  }, [state.messages.length]);

  // Memoize the message list component to prevent unnecessary re-renders
  const MessageListComponent = useMemo(() => {
    if (shouldUseVirtualization) {
      return (
        <VirtualizedMessageList
          messages={state.messages}
          currentUser={state.currentUser}
          onReferenceClick={memoizedHandleReferenceClick}
          height={400}
          itemHeight={80}
        />
      );
    } else {
      return (
        <MessageList 
          messages={state.messages}
          currentUser={state.currentUser}
          onReferenceClick={memoizedHandleReferenceClick}
        />
      );
    }
  }, [state.messages, state.currentUser, memoizedHandleReferenceClick, shouldUseVirtualization]);

  return (
    <div className={`chat-container ${className}`} role="main" aria-label="Chat conversation">
      {/* Messages area */}
      <main 
        className="chat-container__messages" 
        id="main-content"
        aria-label="Chat messages"
      >
        {MessageListComponent}
        <TypingIndicator 
          typingUsers={state.typingUsers}
          currentUser={state.currentUser}
        />
      </main>

      {/* Input area */}
      <div className="chat-container__input" role="complementary" aria-label="Message input area">
        <MessageInput
          onSendMessage={handleSendMessage}
          onVoiceData={handleVoiceData}
          onVoiceStart={onVoiceStart}
          onVoiceStop={onVoiceStop}
          onTypingChange={handleTypingChange}
          disabled={false}
          placeholder="Type a message..."
        />
      </div>
    </div>
  );
};

export default ChatContainer;